import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ReorderRow } from '../../../../applications/ports/versionReportVersionRepository'
import { useVersionReportVersionRepository } from '../../../../services/adaptors/versionReportVersionRepositoryAdaptor'
import {
  sortTargetUuidsByDisplayOrder,
  VersionReportStatus,
  VersionReportVersionEntity,
  VersionReportVersionPageEntity,
} from '../../../../domain/entity/version-report/VersionReportVersionEntity'
import { usePageState } from './pageState'
import { useVersionReportPageDisplayState } from './pageDisplayState'
import { useUrlQuery } from './useUrlQuery'
import { addGlobalMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'
import { getErrorCode } from '../../../../infrastructure/api/error'
import { computeVersionReportDiff } from './versionReportDiff'

export type PendingRowEdit = {
  status?: VersionReportStatus
  comment?: string
  displayOrder?: number
}
export type PendingPageEdit = { pageTitle?: string; leadSentence?: string }
export type PendingEdits = {
  rows: Map<string, PendingRowEdit>
  pages: Map<string, PendingPageEdit>
}

const VERSION_REPORT_STATUSES: readonly VersionReportStatus[] = [
  'SUNNY',
  'CLOUDY',
  'RAINY',
]
const toVersionReportStatus = (
  value: string
): VersionReportStatus | undefined =>
  VERSION_REPORT_STATUSES.find(status => status === value)

export type UseCurrentVersionReturnValue = ReturnType<typeof useCurrentVersion>
export type CurrentReport = {
  versionUuid: string | undefined
  updateVersionUuid: (v: string | undefined) => void
  currentVersion: VersionReportVersionEntity | undefined
  currentPage: VersionReportVersionPageEntity | undefined
}
export const useCurrentVersion = (uuid: string, projectUuid: string) => {
  const [versionUuid, setVersionUuid] = useState<string | undefined>()
  const [currentVersion, setCurrentVersion] = useState<
    VersionReportVersionEntity | undefined
  >()
  const { fetch, reCalculateSnapshot, saveVersionReportBatch } =
    useVersionReportVersionRepository()
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0)
  const [initialized, setInitialized] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [changeLength, setChangeLength] = useState<number>(0)
  const [discardToken, setDiscardToken] = useState<number>(0)
  const dispatch = useDispatch()

  // commit 系が stale closure を避けて最新の currentVersion を読むための ref。
  const currentVersionRef = useRef<VersionReportVersionEntity | undefined>(
    currentVersion
  )
  useEffect(() => {
    currentVersionRef.current = currentVersion
  }, [currentVersion])

  // 未保存の行・ページ編集を蓄積する。保存時にbaselineとのdiffを計算する。
  const pendingEditsRef = useRef<PendingEdits>({
    rows: new Map(),
    pages: new Map(),
  })

  // ロード・保存時のみ更新するサーバー確定値。
  // ページタイトル/リード文は live 表示のため currentVersion を直接書き換えるので、
  // 破棄時にこの baseline から表示を復元する。
  const baselineVersionRef = useRef<VersionReportVersionEntity | undefined>(
    currentVersion
  )

  // サーバー確定値の受け取りを一元化する
  const applyServerVersion = useCallback(
    (version: VersionReportVersionEntity) => {
      setCurrentVersion(version)
      baselineVersionRef.current = version
      pendingEditsRef.current = { rows: new Map(), pages: new Map() }
      setChangeLength(0)
    },
    []
  )

  const { versionUuidFromUrl } = useUrlQuery()
  const {
    storedVersionUuid,
    store,
    initialized: pageStateInitialized,
  } = usePageState(uuid, projectUuid)

  const updateVersionUuid = useCallback(
    (newVersionUuid: string | undefined) => {
      setVersionUuid(newVersionUuid)
    },
    []
  )

  const rawCurrentPage = useMemo(() => {
    if (!currentVersion) return undefined
    return currentVersion.pages[currentPageIndex]
  }, [currentPageIndex, currentVersion])

  const {
    displayState,
    updateDisplayState,
    restoreToken: displayStateRestoreToken,
  } = useVersionReportPageDisplayState(uuid, rawCurrentPage?.templatePageUuid)

  const currentPage = rawCurrentPage

  useEffect(() => {
    const fn = async () => {
      if (!versionUuid) {
        setCurrentVersion(undefined)
        baselineVersionRef.current = undefined
        pendingEditsRef.current = { rows: new Map(), pages: new Map() }
        setChangeLength(0)
        setCurrentPageIndex(0)
        const noDataStored = pageStateInitialized && !storedVersionUuid
        noDataStored && setInitialized(true)
        return
      }
      const response = await fetch(versionUuid)
      if (!response) {
        setVersionUuid(undefined)
        setCurrentVersion(undefined)
        baselineVersionRef.current = undefined
        pendingEditsRef.current = { rows: new Map(), pages: new Map() }
        setChangeLength(0)
        setCurrentPageIndex(0)
        // 削除/欠損した報告書の UUID が ui state に残り続けるのを防ぐ
        store({ versionUuid: '' })
        dispatch(
          addGlobalMessage({
            type: MessageLevel.WARN,
            title: intl.formatMessage({ id: 'versionReport.deleted' }),
          })
        )
        pageStateInitialized && setInitialized(true)
        return
      }
      applyServerVersion(response)
      pageStateInitialized && setInitialized(true)
    }
    fn()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- storedVersionUuid の変化で fetch を再実行しない意図的な除外
  }, [
    fetch,
    versionUuid,
    pageStateInitialized,
    store,
    dispatch,
    applyServerVersion,
  ])

  useEffect(() => {
    if (!pageStateInitialized) return
    if (versionUuidFromUrl) {
      setVersionUuid(versionUuidFromUrl)
    } else if (storedVersionUuid) {
      setVersionUuid(storedVersionUuid)
    }
  }, [
    pageStateInitialized,
    storedVersionUuid,
    setVersionUuid,
    versionUuidFromUrl,
  ])

  useEffect(() => {
    if (!versionUuid) return
    store({ versionUuid })
  }, [versionUuid, store])

  const updatePage = useCallback(
    <
      K extends keyof VersionReportVersionPageEntity,
      V extends VersionReportVersionPageEntity[K],
    >(
      path: K,
      value: V
    ) => {
      setCurrentVersion(prev => {
        if (!prev) return prev
        const newPages = prev.pages.map((page, index) => {
          if (index === currentPageIndex) {
            const newPage = {
              ...page,
            }
            newPage[path] = value
            return newPage
          }
          return page
        })
        return {
          ...prev,
          pages: newPages,
        }
      })
    },
    [currentPageIndex]
  )
  const updatePageTitle = useCallback(
    (newValue: string) => {
      updatePage('pageTitle', newValue)
    },
    [updatePage]
  )
  const updateLeadSentence = useCallback(
    (newValue: string) => {
      updatePage('leadSentence', newValue)
    },
    [updatePage]
  )
  const toNextPage = useCallback(() => {
    if (!currentVersion) return
    setCurrentPageIndex(prev => {
      if (prev === currentVersion.pages.length - 1) {
        return 0
      }
      return prev + 1
    })
  }, [currentVersion])
  const toPrevPage = useCallback(() => {
    if (!currentVersion) return
    setCurrentPageIndex(prev => {
      if (prev === 0) {
        return currentVersion.pages.length - 1
      }
      return prev - 1
    })
  }, [currentVersion])
  const reCalculateAndFetchVersion = useCallback(async () => {
    if (!versionUuid) return
    await reCalculateSnapshot(versionUuid)
    const response = await fetch(versionUuid)
    if (!response) {
      setVersionUuid(undefined)
      setCurrentVersion(undefined)
      baselineVersionRef.current = undefined
      pendingEditsRef.current = { rows: new Map(), pages: new Map() }
      setChangeLength(0)
      setCurrentPageIndex(0)
      return
    }
    applyServerVersion(response)
  }, [versionUuid, reCalculateSnapshot, fetch, applyServerVersion])

  const updateCurrentVersion = useCallback(
    (version: VersionReportVersionEntity) => {
      applyServerVersion(version)
      setCurrentPageIndex(prev =>
        prev >= version.pages.length
          ? Math.max(0, version.pages.length - 1)
          : prev
      )
    },
    [applyServerVersion]
  )

  const refetchVersion = useCallback(async () => {
    if (!versionUuid) return
    const response = await fetch(versionUuid)
    if (response) {
      updateCurrentVersion(response)
    }
  }, [versionUuid, fetch, updateCurrentVersion])

  const isHandlingConflictRef = useRef(false)
  const handleSaveConflict = useCallback(async () => {
    if (isHandlingConflictRef.current) return
    isHandlingConflictRef.current = true
    try {
      const version = currentVersionRef.current
      if (version) {
        const latest = await fetch(version.uuid)
        if (latest) updateCurrentVersion(latest)
      }
      dispatch(
        addGlobalMessage({
          type: MessageLevel.WARN,
          title: intl.formatMessage({ id: 'versionReport.conflict.title' }),
          text: intl.formatMessage({ id: 'versionReport.conflict.message' }),
        })
      )
    } finally {
      isHandlingConflictRef.current = false
    }
  }, [fetch, updateCurrentVersion, dispatch])

  const updateRow = useCallback(
    async (
      rowUuid: string,
      _lockVersion: number,
      key: 'status' | 'comment',
      value: string
    ): Promise<boolean> => {
      const version = currentVersionRef.current
      if (!version) return false
      const baselineRow = version.pages
        .flatMap(p => p.rows)
        .find(r => r.uuid === rowUuid)
      if (!baselineRow) return false

      const pending = pendingEditsRef.current
      const existing = pending.rows.get(rowUuid) ?? {}

      const isRevertingToBaseline = baselineRow[key] === value
      const updated = { ...existing }
      if (isRevertingToBaseline) {
        delete updated[key]
      } else if (key === 'status') {
        // value は grid 側で常に VersionReportStatus を渡すため型を絞り込む
        updated.status = toVersionReportStatus(value) ?? baselineRow.status
      } else {
        updated.comment = value
      }

      if (Object.keys(updated).length === 0) {
        pending.rows.delete(rowUuid)
      } else {
        pending.rows.set(rowUuid, updated)
      }

      setChangeLength(pending.rows.size + pending.pages.size)
      return true
    },
    [] // currentVersionRef は ref なので依存不要
  )

  const applyReorderedRows = useCallback(
    async (pageUuid: string, reorderPayload: ReorderRow[]) => {
      const pending = pendingEditsRef.current
      for (const { rowUuid, displayOrder } of reorderPayload) {
        const version = currentVersionRef.current
        const baselineRow = version?.pages
          .flatMap(p => p.rows)
          .find(r => r.uuid === rowUuid)
        const existing = pending.rows.get(rowUuid) ?? {}
        const updated = { ...existing }

        if (baselineRow && baselineRow.displayOrder === displayOrder) {
          delete updated.displayOrder
        } else {
          updated.displayOrder = displayOrder
        }

        if (Object.keys(updated).length === 0) {
          pending.rows.delete(rowUuid)
        } else {
          pending.rows.set(rowUuid, updated)
        }
      }
      setChangeLength(pending.rows.size + pending.pages.size)
    },
    [] // currentVersionRef は ref なので依存不要
  )

  // overrides は入力欄の最終値 (debounce 未反映分があるため ref ではなく直接受け取る)。
  const commitPageContent = useCallback(
    async (
      pageUuid: string,
      overrides?: { pageTitle?: string; leadSentence?: string | undefined }
    ): Promise<boolean> => {
      // 変更判定の基準はサーバー確定値 (baseline)。
      // currentVersion はタイトル/リード文の live 表示で書き換わるため基準に使えない。
      const baseline = baselineVersionRef.current
      if (!baseline) return false
      const baselinePage = baseline.pages.find(p => p.uuid === pageUuid)
      if (!baselinePage) return false

      const pending = pendingEditsRef.current
      const updated = { ...(pending.pages.get(pageUuid) ?? {}) }

      if (overrides && 'pageTitle' in overrides) {
        if (overrides.pageTitle === baselinePage.pageTitle) {
          delete updated.pageTitle
        } else {
          updated.pageTitle = overrides.pageTitle
        }
      }
      if (overrides && 'leadSentence' in overrides) {
        if (overrides.leadSentence === baselinePage.leadSentence) {
          delete updated.leadSentence
        } else {
          updated.leadSentence = overrides.leadSentence
        }
      }

      if (Object.keys(updated).length === 0) {
        pending.pages.delete(pageUuid)
      } else {
        pending.pages.set(pageUuid, updated)
      }

      setChangeLength(pending.rows.size + pending.pages.size)
      return true
    },
    [] // baselineVersionRef/pendingEditsRef は ref なので依存不要
  )

  const isDirty = changeLength > 0

  const save = useCallback(async (): Promise<boolean> => {
    // ページタイトル/リード文は live 表示で currentVersion を書き換えるため、
    // diff・overlay の基準にはサーバー確定値 (baseline) を使う
    const baseline = baselineVersionRef.current
    if (!baseline) return false

    const pending = pendingEditsRef.current
    if (pending.rows.size === 0 && pending.pages.size === 0) return true

    const overlay: VersionReportVersionEntity = {
      ...baseline,
      pages: baseline.pages.map(page => {
        const pagePending = pending.pages.get(page.uuid)
        const rows = page.rows.map(row => {
          const rowPending = pending.rows.get(row.uuid)
          if (!rowPending) return row
          return {
            ...row,
            ...(rowPending.status !== undefined && {
              status: rowPending.status,
            }),
            ...(rowPending.comment !== undefined && {
              comment: rowPending.comment,
            }),
            ...(rowPending.displayOrder !== undefined && {
              displayOrder: rowPending.displayOrder,
            }),
          }
        })
        const hasDisplayOrderEdit = page.rows.some(
          row => pending.rows.get(row.uuid)?.displayOrder !== undefined
        )
        return {
          ...page,
          ...(pagePending?.pageTitle !== undefined && {
            pageTitle: pagePending.pageTitle,
          }),
          ...(pagePending &&
            'leadSentence' in pagePending && {
              leadSentence: pagePending.leadSentence,
            }),
          ...(hasDisplayOrderEdit && {
            config: {
              ...page.config,
              tableRows: sortTargetUuidsByDisplayOrder(rows),
            },
          }),
          rows,
        }
      }),
    }

    const diff = computeVersionReportDiff(baseline, overlay)
    if (diff.pages.length === 0 && diff.rows.length === 0) return true

    setIsSaving(true)
    try {
      const response = await saveVersionReportBatch(diff)

      const newLockVersionByPageUuid = new Map(
        response.pages.map(p => [p.uuid, p.lockVersion])
      )
      const newLockVersionByRowUuid = new Map(
        response.rows.map(r => [r.uuid, r.lockVersion])
      )

      const newVersion: VersionReportVersionEntity = {
        ...overlay,
        pages: overlay.pages.map(page => ({
          ...page,
          lockVersion:
            newLockVersionByPageUuid.get(page.uuid) ?? page.lockVersion,
          rows: page.rows.map(row => ({
            ...row,
            lockVersion:
              newLockVersionByRowUuid.get(row.uuid) ?? row.lockVersion,
          })),
        })),
      }

      applyServerVersion(newVersion)
      dispatch(
        addGlobalMessage({
          type: MessageLevel.SUCCESS,
          title: intl.formatMessage({ id: 'registration.complete' }),
        })
      )
      return true
    } catch (e) {
      if (getErrorCode(e) === 'CONSISTENCY_VIOLATION') {
        await handleSaveConflict()
        return false
      }
      throw e
    } finally {
      setIsSaving(false)
    }
  }, [saveVersionReportBatch, applyServerVersion, handleSaveConflict, dispatch])

  const discardDraft = useCallback(() => {
    pendingEditsRef.current = { rows: new Map(), pages: new Map() }
    setChangeLength(0)
    // discardToken を増やすことで grids の tableContents を再計算させ、
    // 未保存編集を消したベースライン値を表示に反映する
    setDiscardToken(t => t + 1)
    // ページタイトル・リード文は live 表示で currentVersion を書き換えているため、
    // サーバー確定値 (baseline) に戻して未保存の入力を破棄する
    const baseline = baselineVersionRef.current
    if (baseline) {
      setCurrentVersion(baseline)
    }
  }, [])

  return {
    versionUuid,
    updateVersionUuid,
    currentPage,
    currentPageIndex,
    currentVersion,
    updateCurrentVersion,
    updatePageTitle,
    updateLeadSentence,
    updateRow,
    applyReorderedRows,
    commitPageContent,
    toNextPage,
    toPrevPage,
    reCalculateAndFetchVersion,
    refetchVersion,
    storedVersionUuid,
    initialized,
    displayState,
    updateDisplayState,
    displayStateRestoreToken,
    save,
    discardDraft,
    isDirty,
    changeLength,
    isSaving,
    pendingEditsRef,
    discardToken,
  }
}
